import React from 'react';
import { FileInput, Switch, TextInput } from '@mantine/core';
import { Button } from '../../../components/Button';
import type { TenantView } from '../../../services/tenants/tenants.service';
import type { E2eRunnerController } from '../useE2eRunnerController';
import { E2eRunControls } from './E2eRunControls';
import classes from '../tenants-page.module.scss';

type TenantsTableProps = {
  tenants: TenantView[];
  isLoading: boolean;
  updatingTenantIds: number[];
  expandedTenantId: number | null;
  onToggleExpanded: (tenantId: number) => void;
  onToggleTenantActive: (tenant: TenantView) => void;
  getTenantOwnerEmailDraft: (tenant: TenantView) => string;
  setTenantOwnerEmailDraft: (tenantIdPk: number, value: string) => void;
  getTenantIsTestDraft: (tenant: TenantView) => boolean;
  setTenantIsTestDraft: (tenantIdPk: number, value: boolean) => void;
  getTenantRestigoBranchDraft: (tenant: TenantView) => string;
  setTenantRestigoBranchDraft: (tenantIdPk: number, value: string) => void;
  getTenantRestigoKeyDraft: (tenant: TenantView) => string | undefined;
  setTenantRestigoKeyDraft: (tenantIdPk: number, value: string) => void;
  getTenantOntopoKeyDraft: (tenant: TenantView) => string;
  setTenantOntopoKeyDraft: (tenantIdPk: number, value: string) => void;
  getStagedPhotoFile: (tenant: TenantView) => File | null;
  getTenantPhotoPreviewUrl: (tenant: TenantView) => string | null;
  showRemovePhotoControl: (tenant: TenantView) => boolean;
  hasTenantPanelChanges: (tenant: TenantView) => boolean;
  onStageTenantPhoto: (tenant: TenantView, file: File | null) => void;
  onStageRemoveTenantPhoto: (tenant: TenantView) => void;
  onSaveTenantPanel: (tenant: TenantView) => void;
  e2eRunner: E2eRunnerController;
};

export const TenantsTable: React.FC<TenantsTableProps> = ({
  tenants,
  isLoading,
  updatingTenantIds,
  expandedTenantId,
  onToggleExpanded,
  onToggleTenantActive,
  getTenantOwnerEmailDraft,
  setTenantOwnerEmailDraft,
  getTenantIsTestDraft,
  setTenantIsTestDraft,
  getTenantRestigoBranchDraft,
  setTenantRestigoBranchDraft,
  getTenantRestigoKeyDraft,
  setTenantRestigoKeyDraft,
  getTenantOntopoKeyDraft,
  setTenantOntopoKeyDraft,
  getStagedPhotoFile,
  getTenantPhotoPreviewUrl,
  showRemovePhotoControl,
  hasTenantPanelChanges,
  onStageTenantPhoto,
  onStageRemoveTenantPhoto,
  onSaveTenantPanel,
  e2eRunner,
}) => {
  return (
    <div className={classes.tableWrap}>
      <table className={classes.table}>
        <thead>
          <tr>
            <th>שם החברה</th>
            <th>מזהה חברה</th>
            <th>סטטוס</th>
            <th>מע"מ</th>
            <th>נוצר בתאריך</th>
            <th>פעולה</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((tenant) => {
            const isUpdating = updatingTenantIds.includes(tenant.id);
            const isExpanded = expandedTenantId === tenant.id;
            const photoPreviewUrl = getTenantPhotoPreviewUrl(tenant);
            return (
              <React.Fragment key={tenant.id}>
                <tr
                  className={isExpanded ? `${classes.row} ${classes.expanded}` : classes.row}
                  onClick={() => onToggleExpanded(tenant.id)}
                  data-testid={`tenants-row-${tenant.id}`}
                >
                  <td data-label="שם החברה">{tenant.companyName}</td>
                  <td data-label="מזהה חברה">{tenant.tenantId}</td>
                  <td data-label="סטטוס">
                    <span className={tenant.isActive ? classes.statusActive : classes.statusInactive}>
                      {tenant.isActive ? 'פעילה' : 'מושבתת'}
                    </span>
                  </td>
                  <td data-label='מע"מ' className={classes.vatCell}>
                    <span>{tenant.vatRate}%</span>
                  </td>
                  <td data-label="נוצר בתאריך">{new Date(tenant.createdAt).toLocaleDateString('he-IL')}</td>
                  <td data-label="פעולה" className={classes.actionsCell}>
                    <Button
                      variant={tenant.isActive ? 'cancel' : 'save'}
                      size="sm"
                      loading={isUpdating}
                      loadingLabel="שומר..."
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggleTenantActive(tenant);
                      }}
                      dataTestId={`tenants-toggle-active-${tenant.id}`}
                    >
                      {tenant.isActive ? 'השבתה' : 'הפעלה'}
                    </Button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className={classes.settingsRow}>
                    <td colSpan={6}>
                      <div className={classes.settingsPanel} onClick={(event) => event.stopPropagation()}>
                        <div className={classes.settingsGrid}>
                          <TextInput
                            type="email"
                            label="מייל בעלים (קוד זיכוי מנהל)"
                            placeholder="owner@company.com"
                            value={getTenantOwnerEmailDraft(tenant)}
                            onChange={(event) =>
                              setTenantOwnerEmailDraft(tenant.id, event.currentTarget.value)
                            }
                            data-testid={`tenants-owner-email-${tenant.id}`}
                          />
                          <Switch
                            label="חברת בדיקה (E2E)"
                            checked={getTenantIsTestDraft(tenant)}
                            onChange={(event) =>
                              setTenantIsTestDraft(tenant.id, event.currentTarget.checked)
                            }
                            data-testid={`tenants-is-test-${tenant.id}`}
                                                      />
                          {tenant.isTest && <E2eRunControls tenantId={tenant.id} e2eRunner={e2eRunner} />}
                          <TextInput
                            type="number"
                            label="מזהה סניף רסטיגו"
                            placeholder="642"
                            value={getTenantRestigoBranchDraft(tenant)}
                            onChange={(event) =>
                              setTenantRestigoBranchDraft(tenant.id, event.currentTarget.value)
                            }
                            data-testid={`tenants-restigo-branch-${tenant.id}`}
                          />
                          <TextInput
                            label="מפתח רסטיגו"
                            placeholder={tenant.restigoKeyMasked ?? 'לא הוגדר'}
                            value={getTenantRestigoKeyDraft(tenant) ?? ''}
                            onChange={(event) =>
                              setTenantRestigoKeyDraft(tenant.id, event.currentTarget.value)
                            }
                            data-testid={`tenants-restigo-key-${tenant.id}`}
                          />
                        </div>
                        <div className={classes.photoSlot}>
                          <span className={classes.photoLabel}>תמונת החברה</span>
                          <div className={classes.photoRow}>
                            <span className={classes.photoPreview}>
                              {photoPreviewUrl ? (
                                <img
                                  src={photoPreviewUrl}
                                  alt={tenant.companyName}
                                  data-testid={`tenants-photo-preview-${tenant.id}`}
                                />
                              ) : (
                                <span className={classes.photoPlaceholder}>—</span>
                              )}
                            </span>
                            <div className={classes.photoControls}>
                              <FileInput
                                accept="image/jpeg,image/png,image/webp"
                                placeholder="בחר תמונה"
                                radius="md"
                                clearable
                                disabled={isUpdating}
                                aria-label="תמונת החברה"
                                value={getStagedPhotoFile(tenant)}
                                data-testid={`tenants-photo-upload-${tenant.id}`}
                                onChange={(file) => onStageTenantPhoto(tenant, file)}
                              />
                              {showRemovePhotoControl(tenant) && (
                                <Button
                                  variant="cancel"
                                  size="sm"
                                  disabled={isUpdating}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onStageRemoveTenantPhoto(tenant);
                                  }}
                                  dataTestId={`tenants-photo-remove-${tenant.id}`}
                                >
                                  הסר תמונה
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={classes.settingsGrid}>
                          <TextInput
                            label="מפתח אונטופו"
                            placeholder={tenant.ontopoKeyMasked ?? 'ללא מפתח'}
                            value={getTenantOntopoKeyDraft(tenant)}
                            onChange={(event) =>
                              setTenantOntopoKeyDraft(tenant.id, event.currentTarget.value)
                            }
                            data-testid={`tenants-ontopo-key-${tenant.id}`}
                          />
                        </div>
                        <div className={classes.settingsActions}>
                          <Button
                            variant="save"
                            size="sm"
                            loading={isUpdating}
                            loadingLabel="שומר..."
                            disabled={!hasTenantPanelChanges(tenant)}
                            onClick={(event) => {
                              event.stopPropagation();
                              onSaveTenantPanel(tenant);
                            }}
                            dataTestId={`tenants-save-owner-email-${tenant.id}`}
                          >
                            שמור הכל
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {tenants.length === 0 && !isLoading && (
            <tr>
              <td colSpan={6} className={classes.empty}>
                אין חברות עדיין. לחץ "צור חברה" כדי להוסיף את הראשונה.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
